import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import { ADMIN_ERROR_MESSAGES } from '../errors';

// ============================================================================
// Types
// ============================================================================

export interface UpdateUserStatusResult {
  success: boolean;
  error?: string;
}

export interface UpdateUserStatusParams {
  userId: string;
  isActive: boolean;
}

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: ADMIN_ERROR_MESSAGES.DEFAULT_ERROR,
  NETWORK_ERROR: ADMIN_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: ADMIN_ERROR_MESSAGES.UNEXPECTED_ERROR,
} as const;

// ============================================================================
// Error Extraction
// ============================================================================

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  // Handle string errors
  if (typeof errorData === 'string') {
    return errorData;
  }

  // Handle object errors - backend returns { message: "..." } or { error: "..." }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const errorMessage = apiError.message || apiError.error;

    if (errorMessage) {
      return errorMessage;
    }

    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

// ============================================================================
// API Service
// ============================================================================

export const userStatusUpdateApiService = {
  /**
   * Updates user status (activate/deactivate)
   * @param params - User ID and active status
   * @returns Result with success status and optional error message
   */
  updateUserStatus: async (
    params: UpdateUserStatusParams
  ): Promise<UpdateUserStatusResult> => {
    try {
      const { userId, isActive } = params;

      const result = await apiService.patchAPI(
        `loopay-user-management/users/${userId}/status`,
        { isActive },
        { skipGlobalErrorRedirect: true }
      );

      // API returns 204 No Content on success (no body)
      // 204 is a success status code
      if (!result.error || result.status === 204) {
        return {
          success: true,
        };
      } else {
        // Handle error from apiService
        const errorData = result.data;
        const isNetworkError = result.isNetworkError;
        const statusCode = result.status;

        // Handle specific HTTP status codes
        if (statusCode === 404) {
          return {
            success: false,
            error: 'Użytkownik nie został znaleziony.',
          };
        }

        if (statusCode === 400) {
          return {
            success: false,
            error: 'Proszę podać prawidłowe dane wejściowe.',
          };
        }

        if (statusCode === 500) {
          return {
            success: false,
            error: 'Coś poszło nie tak. Spróbuj ponownie.',
          };
        }

        // Extract error message from backend response
        const errorMessage = extractErrorMessage(errorData);

        // Handle network errors
        if (isNetworkError) {
          return {
            success: false,
            error: ERROR_MESSAGES.NETWORK_ERROR,
          };
        }

        return {
          success: false,
          error: errorMessage || ERROR_MESSAGES.DEFAULT_ERROR,
        };
      }
    } catch (error) {
      // Handle unexpected errors
      if (error instanceof AxiosError) {
        const errorData = error.response?.data;
        const isNetworkError = !error.response && !!error.request;
        const statusCode = error.response?.status;

        // Handle network errors
        if (isNetworkError) {
          return {
            success: false,
            error: ERROR_MESSAGES.NETWORK_ERROR,
          };
        }

        // Handle specific HTTP status codes
        if (statusCode === 404) {
          return {
            success: false,
            error: 'Użytkownik nie został znaleziony.',
          };
        }

        if (statusCode === 400) {
          return {
            success: false,
            error: 'Proszę podać prawidłowe dane wejściowe.',
          };
        }

        if (statusCode === 500) {
          return {
            success: false,
            error: 'Coś poszło nie tak. Spróbuj ponownie później.',
          };
        }

        // Extract error message from backend response
        const errorMessage = extractErrorMessage(errorData);

        return {
          success: false,
          error: errorMessage || ERROR_MESSAGES.DEFAULT_ERROR,
        };
      }

      // Handle non-Axios errors
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : ERROR_MESSAGES.UNEXPECTED_ERROR,
      };
    }
  },
};
